/**
 * ClubAdminRequests — admin queue of all Club Social visit requests.
 *
 * Path: /club/admin/requests
 * Auth: shares the /mira/console login (lc_console_token).
 *
 * FIR-20260611-001 · Phase 4
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useConsoleAuth, consoleApi } from "@/lib/console-auth";
import ClubLayout from "./ClubLayout";

function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
    return `${Math.floor(diff / 86400)} d ago`;
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    new: { bg: "rgba(46,157,157,0.12)", fg: "#1F7878" },
    sent: { bg: "rgba(127,174,60,0.18)", fg: "#3d6010" },
    error: { bg: "rgba(180,68,31,0.18)", fg: "#7a2c12" },
  };
  const s = map[status] || { bg: "rgba(35,48,42,0.07)", fg: "#23302A" };
  return (
    <span style={{
      background: s.bg, color: s.fg, fontSize: 11,
      letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
      padding: "4px 9px", borderRadius: 100,
    }}>{status || "—"}</span>
  );
}

export default function ClubAdminRequests() {
  const { user, loading: authLoading, logout } = useConsoleAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // FIR-20260623-RequestExpand · admin clicks a row to read the full
  // request body (services / dates / notes / Zoho delivery log).
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({}); // id → full doc
  const [detailLoading, setDetailLoading] = useState(null);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/mira/console/login?return=/club/admin/requests", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await consoleApi.get("/club/admin/requests?limit=200");
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  async function toggleExpand(req) {
    if (expandedId === req.id) {
      setExpandedId(null); setDetailError(null); return;
    }
    setExpandedId(req.id);
    setDetailError(null);
    if (detailCache[req.id]) return; // already fetched
    setDetailLoading(req.id);
    try {
      const r = await consoleApi.get(`/club/admin/requests/${req.id}`);
      setDetailCache((c) => ({ ...c, [req.id]: r.data }));
    } catch (e) {
      setDetailError(e.response?.data?.detail || e.message);
    } finally {
      setDetailLoading(null);
    }
  }

  const fakeMember = useMemo(() => (user ? { first: user.first || "Admin", last: user.last || "" } : null), [user]);

  if (authLoading || !user) {
    return (
      <ClubLayout member={fakeMember}>
        <main className="wrap" style={{ padding: "60px 0" }}>
          <div className="loading">Checking access…</div>
        </main>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout member={fakeMember} onSignOut={logout}>
      <main className="wrap" style={{ paddingTop: 36, paddingBottom: 80 }}>
        <section className="hero" style={{ padding: "20px 0 30px" }}>
          <div className="kicker">Club Social® · Admin queue</div>
          <h1 className="serif" style={{ fontSize: "clamp(28px, 4vw, 44px)" }}>
            Incoming visit requests
          </h1>
          <p>Live feed of every Club Social visit request — most recent first.</p>
          <div style={{ display: "flex", gap: 22, marginTop: 18, flexWrap: "wrap" }}>
            <div>
              <b className="serif" style={{ fontSize: 28, lineHeight: 1 }} data-testid="admin-req-total">
                {loading ? "…" : data.total.toLocaleString()}
              </b>
              <div style={{ fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase", color: "var(--muted)", marginTop: 4 }}>
                Total requests
              </div>
            </div>
            <Link to="/club" style={{ alignSelf: "center", color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
                  data-testid="admin-back-to-club">
              ← back to directory
            </Link>
            <Link to="/club/admin/clubs" style={{ alignSelf: "center", color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
                  data-testid="admin-req-to-clubs">
              → Clubs
            </Link>
            <Link to="/club/admin/members" style={{ alignSelf: "center", color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}
                  data-testid="admin-req-to-members">
              → Members
            </Link>
          </div>
        </section>

        {error && (
          <div className="empty" data-testid="admin-error">
            <div className="serif">Couldn&apos;t load the queue</div>
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="empty">
            <div className="serif">No requests yet.</div>
            When a member submits a visit, it appears here.
          </div>
        ) : (
          <div style={{
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 14, overflow: "hidden",
            boxShadow: "var(--shadow)",
          }} data-testid="admin-req-table">
            <div style={{
              display: "grid", gridTemplateColumns: "140px 1fr 1fr 110px 120px 100px",
              alignItems: "center", padding: "14px 18px",
              background: "rgba(35,48,42,0.04)",
              borderBottom: "1px solid var(--line)",
              fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase",
              color: "var(--muted)", fontWeight: 700, gap: 14,
            }}>
              <div>Reference</div>
              <div>Member</div>
              <div>Club</div>
              <div>Party</div>
              <div>Status</div>
              <div style={{ textAlign: "right" }}>Received</div>
            </div>
            {data.items.map((r) => {
              const isOpen = expandedId === r.id;
              const detail = detailCache[r.id];
              return (
              <div key={r.id} data-testid={`admin-req-row-wrap-${r.ref}`}>
                <div role="button" tabIndex={0}
                     onClick={() => toggleExpand(r)}
                     onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpand(r); } }}
                     data-testid={`admin-req-row-${r.ref}`}
                     style={{
                       display: "grid",
                       gridTemplateColumns: "140px 1fr 1fr 110px 120px 100px",
                       alignItems: "center",
                       padding: "16px 18px",
                       borderBottom: isOpen ? "1px solid var(--line)" : "1px solid var(--line-2)",
                       gap: 14, fontSize: 14, cursor: "pointer",
                       background: isOpen ? "rgba(46,157,157,0.06)" : "transparent",
                     }}>
                  <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 600, color: "var(--teal-d)",
                                display: "flex", alignItems: "center", gap: 6 }}>
                    <span aria-hidden="true" style={{
                        display: "inline-block", transition: "transform 150ms ease",
                        transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                        opacity: 0.6, fontSize: 11,
                    }}>▶</span>
                    {r.ref}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {r.member?.first} {r.member?.last}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                      {r.member?.card_no}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 500 }}>{r.club}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>
                      {r.club_city}{r.club_country ? `, ${r.club_country}` : ""}
                    </div>
                  </div>
                  <div>
                    {r.party?.total || 1} {r.party?.total === 1 ? "person" : "people"}
                  </div>
                  <div>
                    <StatusPill status={r.last_delivery_event?.status || r.status} />
                  </div>
                  <div style={{ textAlign: "right", color: "var(--muted)", fontSize: 12.5 }}>
                    {relTime(r.created_at)}
                  </div>
                </div>
                {isOpen && (
                  <RequestDetailPanel
                    listRow={r}
                    detail={detail}
                    loading={detailLoading === r.id}
                    error={detailError}
                  />
                )}
              </div>
              );
            })}
          </div>
        )}
      </main>
    </ClubLayout>
  );
}

/* ──────────────────────────────────────────────────────────────────
   Expanded detail block · shown inline below a clicked request row.
   ────────────────────────────────────────────────────────────────── */
function RequestDetailPanel({ listRow, detail, loading, error }) {
  // Prefer freshly-fetched detail; fall back to listRow if still loading.
  const r = detail || listRow;
  const services = Array.isArray(r.services) ? r.services : [];
  const log = detail ? (detail.delivery_log || []) : [];

  return (
    <div data-testid={`admin-req-detail-${r.ref}`}
         style={{
           padding: "20px 22px 26px",
           background: "rgba(35,48,42,0.025)",
           borderBottom: "1px solid var(--line-2)",
           fontSize: 13.5, lineHeight: 1.55,
         }}>
      {loading && (
        <div style={{ color: "var(--muted)", fontStyle: "italic" }}>
          Loading delivery details…
        </div>
      )}
      {error && (
        <div style={{ color: "#7a2c12", marginBottom: 12 }}>
          Couldn&apos;t load full detail · {error}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
        {/* MEMBER ─────────────────────────────────────────── */}
        <DetailBlock title="Member">
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            {r.member?.photo_url && (
              <img src={r.member.photo_url} alt=""
                   style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover", border: "1px solid var(--line-2)" }} />
            )}
            <div>
              <div style={{ fontWeight: 600 }}>{r.member?.first} {r.member?.last}</div>
              <div style={{ color: "var(--muted)", fontSize: 12.5 }}>{r.member?.card_no}</div>
              {r.member?.email && (
                <div style={{ marginTop: 4 }}>
                  <a href={`mailto:${r.member.email}`} style={{ color: "var(--teal-d)" }}>{r.member.email}</a>
                </div>
              )}
              {r.member?.phone && (
                <div>
                  <a href={`tel:${r.member.phone}`} style={{ color: "var(--teal-d)" }}>{r.member.phone}</a>
                </div>
              )}
              {(r.member?.resident_city || r.member?.resident_state) && (
                <div style={{ color: "var(--muted)", fontSize: 12.5, marginTop: 2 }}>
                  {[r.member?.resident_city, r.member?.resident_state].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          </div>
        </DetailBlock>

        {/* CLUB & PARTY ───────────────────────────────────── */}
        <DetailBlock title="Club & Party">
          <div style={{ fontWeight: 500 }}>{r.club}</div>
          <div style={{ color: "var(--muted)", fontSize: 12.5 }}>
            {r.club_city}{r.club_country ? `, ${r.club_country}` : ""}
          </div>
          <div style={{ marginTop: 8 }}>
            <b>{r.party?.total || 1}</b>{" "}
            {r.party?.total === 1 ? "person" : "people"}
            <div style={{ color: "var(--muted)", fontSize: 12.5 }}>
              Member: {r.party?.member ?? 1}
              {r.party?.spouse ? " · Spouse: 1" : ""}
              {r.party?.children ? ` · Children: ${r.party.children}` : ""}
              {r.party?.guests ? ` · Guests: ${r.party.guests}` : ""}
            </div>
          </div>
        </DetailBlock>

        {/* SERVICES (full width) ──────────────────────────── */}
        <DetailBlock title="Requested services" colSpan={2}>
          {services.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic" }}>No service items captured.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {services.map((s, i) => (
                <div key={i} style={{
                  background: "#fff", border: "1px solid var(--line-2)",
                  borderRadius: 8, padding: "10px 12px",
                }}>
                  <div style={{ fontWeight: 600, color: "var(--teal-d)" }}>{s.service || "—"}</div>
                  {s.details && typeof s.details === "object" && (
                    <div style={{ marginTop: 4, display: "grid",
                                  gridTemplateColumns: "max-content 1fr",
                                  rowGap: 2, columnGap: 14, fontSize: 13 }}>
                      {Object.entries(s.details).map(([k, v]) =>
                        v == null || v === "" ? null : (
                          <React.Fragment key={k}>
                            <span style={{ color: "var(--muted)", textTransform: "capitalize" }}>{k.replace(/_/g, " ")}:</span>
                            <span>{String(v)}</span>
                          </React.Fragment>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DetailBlock>

        {/* MESSAGE / NOTES ────────────────────────────────── */}
        {r.other && (
          <DetailBlock title="Member note" colSpan={2}>
            <div style={{ background: "#fff", border: "1px solid var(--line-2)",
                          borderRadius: 8, padding: "10px 12px",
                          fontStyle: "italic", whiteSpace: "pre-wrap" }}>
              {r.other}
            </div>
          </DetailBlock>
        )}

        {/* DELIVERY LOG ──────────────────────────────────── */}
        <DetailBlock title="Delivery log" colSpan={2}>
          {!detail ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic" }}>
              {loading ? "Loading…" : "Click row to load full log."}
            </div>
          ) : log.length === 0 ? (
            <div style={{ color: "var(--muted)", fontStyle: "italic" }}>
              No delivery events yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {log.map((e, i) => (
                <div key={i} style={{
                  background: "#fff", border: "1px solid var(--line-2)",
                  borderRadius: 8, padding: "8px 12px", fontSize: 12.5,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <b>{e.event}</b>
                    <span style={{ color: "var(--muted)" }}>{new Date(e.timestamp).toLocaleString()}</span>
                  </div>
                  {e.subject && <div style={{ marginTop: 2 }}>{e.subject}</div>}
                  {e.provider_id && (
                    <div style={{ color: "var(--muted)" }}>
                      Zoho ticket #{e.provider_id}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DetailBlock>

        {/* META ──────────────────────────────────────────── */}
        <DetailBlock title="Timeline & terms" colSpan={2}>
          <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr",
                        rowGap: 4, columnGap: 14, fontSize: 12.5 }}>
            <span style={{ color: "var(--muted)" }}>Created:</span>
            <span>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
            <span style={{ color: "var(--muted)" }}>Updated:</span>
            <span>{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</span>
            <span style={{ color: "var(--muted)" }}>Source:</span>
            <span>{r.source || "—"}</span>
            <span style={{ color: "var(--muted)" }}>Terms accepted:</span>
            <span>{r.terms_accepted ? `Yes (${r.terms_version || "—"})` : "No"}</span>
            {r.handicap_index != null && (
              <>
                <span style={{ color: "var(--muted)" }}>Handicap:</span>
                <span>{r.handicap_index}{r.handicap_proof_filename ? ` · proof: ${r.handicap_proof_filename}` : ""}</span>
              </>
            )}
          </div>
        </DetailBlock>
      </div>
    </div>
  );
}

function DetailBlock({ title, children, colSpan }) {
  return (
    <div style={colSpan === 2 ? { gridColumn: "span 2" } : undefined}>
      <div style={{
        fontSize: 11, letterSpacing: "0.13em", textTransform: "uppercase",
        color: "var(--muted)", fontWeight: 700, marginBottom: 6,
      }}>{title}</div>
      {children}
    </div>
  );
}
